import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import {
  ArrowRight, Calendar, MessageSquare, Shield, Star, Users,
  Clock, CreditCard, Award, X, CheckCircle2, Briefcase
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { SEOHead, organizationSchema, websiteSchema } from "@/components/SEOHead";

const LOGO_URL = "/logo-horizontal.webp";

const IMG = {
  heroMain: "https://static.prod-images.emergentagent.com/jobs/c344d8a5-a742-44d8-87bd-3ab737116174/images/e2a9e6934554c124061a0072275cb124f863feb0e2afcea7a95584fabbcfced5.png",
  heroAlt: "https://images.unsplash.com/photo-1768849352374-f50f9e304a4d?fm=webp&w=640&q=75",
  chef: "https://images.unsplash.com/photo-1761416376088-d6456fcd76fd?fm=webp&w=800&q=75",
  bartender: "https://images.pexels.com/photos/4485370/pexels-photo-4485370.jpeg?auto=compress&cs=tinysrgb&w=800&fm=webp",
  waiter: "https://images.pexels.com/photos/6817133/pexels-photo-6817133.jpeg?auto=compress&cs=tinysrgb&w=800&fm=webp",
  caterer: "https://images.pexels.com/photos/7648322/pexels-photo-7648322.jpeg?auto=compress&cs=tinysrgb&w=640&fm=webp",
};

const BUSINESS_STEPS = [
  { num: "01", title: "Post Your Shift", desc: "Describe your needs — roles, times, pay rate. Our tiered rush fee system handles last-minute bookings automatically.", icon: Calendar },
  { num: "02", title: "Review & Accept", desc: "Browse applicants with ratings, badges, and work history. Pick your favorites or let auto-matching handle it.", icon: Users },
  { num: "03", title: "Manage & Pay", desc: "Track shift completion, release payments securely through escrow, and rate workers for next time.", icon: CreditCard },
];

const WORKER_STEPS = [
  { num: "01", title: "Browse Shifts", desc: "See available shifts near you — catering gigs, bartending, serving, event setup, and more.", icon: Briefcase },
  { num: "02", title: "Apply & Confirm", desc: "One tap to apply. Get confirmed instantly and receive all the shift details you need.", icon: CheckCircle2 },
  { num: "03", title: "Work & Earn", desc: "Show up, do great work, build your reputation. Payments released securely after completion.", icon: Award },
];

const FEATURES = [
  { icon: Calendar, title: "Multi-Role Shift Posting", desc: "Post shifts for servers, bartenders, chefs, and event setup — all in one go with flexible scheduling and rush fees." },
  { icon: MessageSquare, title: "Real-Time Messaging", desc: "Communicate directly with staff or businesses. Read receipts, contact masking, and instant push notifications." },
  { icon: Shield, title: "Secure Escrow Payments", desc: "Funds held securely until shift completion. Workers get paid fast. Businesses get peace of mind." },
  { icon: Star, title: "Ratings & Milestone Badges", desc: "Build reputation through verified reviews. Unlock badges at milestones — 10, 50, 100+ shifts completed." },
];

export default function LandingPage() {
  const { user, login, loginEmail, registerEmail } = useAuth();
  const navigate = useNavigate();
  const [showAuth, setShowAuth] = useState(false);
  const [authMode, setAuthMode] = useState("signin");
  const [authForm, setAuthForm] = useState({ name: "", email: "", password: "" });
  const [authLoading, setAuthLoading] = useState(false);
  const [activeTrack, setActiveTrack] = useState("business");

  useEffect(() => {
    if (user) {
      if (!user.role) navigate("/role-selection", { replace: true });
      else navigate("/dashboard", { replace: true });
    }
  }, [user, navigate]);

  const openAuth = (mode) => { setAuthMode(mode); setShowAuth(true); };

  const handleEmailAuth = async (e) => {
    e.preventDefault();
    setAuthLoading(true);
    try {
      const fn = authMode === "signup" ? registerEmail : loginEmail;
      const args = authMode === "signup" ? [authForm.name, authForm.email, authForm.password] : [authForm.email, authForm.password];
      const res = await fn(...args);
      setShowAuth(false);
      navigate(!res.role ? "/role-selection" : "/dashboard");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Authentication failed");
    } finally {
      setAuthLoading(false);
    }
  };

  const handleGoogleFromModal = () => { setShowAuth(false); login(); };

  const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });

  const steps = activeTrack === "business" ? BUSINESS_STEPS : WORKER_STEPS;
  const trackImage = activeTrack === "business" ? IMG.chef : IMG.bartender;

  return (
    <div className="min-h-screen bg-[#F5F7FA] text-[#003973]" data-testid="landing-page">
      <SEOHead
        path="/"
        description="Servello connects businesses with vetted catering, bartending, and event staff. Post shifts, get matched, manage everything — staffing made easy for hospitality and events."
        jsonLd={[organizationSchema, websiteSchema]}
      />

      {/* ─── HEADER ─── */}
      <header className="fixed top-0 left-0 right-0 z-50 backdrop-blur-xl bg-[#F5F7FA]/80 border-b border-[#003973]/10" data-testid="landing-header">
        <div className="max-w-7xl mx-auto px-6 md:px-12 h-16 flex items-center justify-between">
          <img src={LOGO_URL} alt="Servello" className="h-12 object-contain" data-testid="landing-logo" />
          <nav className="hidden md:flex items-center gap-8">
            <button onClick={() => scrollTo("how-it-works")} className="text-sm font-medium text-[#4A6380] hover:text-[#003973] transition-colors" data-testid="landing-nav-how-it-works">How it Works</button>
            <button onClick={() => scrollTo("features")} className="text-sm font-medium text-[#4A6380] hover:text-[#003973] transition-colors" data-testid="landing-nav-features">Features</button>
            <button onClick={() => navigate("/directory")} className="text-sm font-medium text-[#4A6380] hover:text-[#003973] transition-colors" data-testid="landing-nav-directory">Directory</button>
            <button onClick={() => navigate("/blog")} className="text-sm font-medium text-[#4A6380] hover:text-[#003973] transition-colors" data-testid="landing-nav-blog">Blog</button>
            <button onClick={() => navigate("/for-workers")} className="text-sm font-medium text-[#4A6380] hover:text-[#003973] transition-colors" data-testid="landing-nav-for-workers">For Workers</button>
            <button onClick={() => navigate("/for-businesses")} className="text-sm font-medium text-[#4A6380] hover:text-[#003973] transition-colors" data-testid="landing-nav-for-businesses">For Businesses</button>
          </nav>
          <div className="flex items-center gap-3">
            {user ? (
              <button onClick={() => navigate("/dashboard")} className="px-6 py-2.5 rounded-sm bg-[#00B2A9] text-white text-sm font-medium hover:bg-[#00958D] transition-colors" data-testid="landing-dashboard-btn">Dashboard</button>
            ) : (
              <>
                <button onClick={() => openAuth("signin")} className="text-sm font-medium text-[#003973] hover:opacity-70 transition-opacity" data-testid="landing-login-btn">Log in</button>
                <button onClick={() => openAuth("signup")} className="px-6 py-2.5 rounded-sm bg-[#00B2A9] text-white text-sm font-medium hover:bg-[#00958D] transition-colors" data-testid="landing-signup-btn">Sign Up</button>
              </>
            )}
          </div>
        </div>
      </header>

      {/* ─── HERO ─── */}
      <section className="pt-28 pb-16 md:pt-36 md:pb-24 px-6 md:px-12 lg:px-24" data-testid="hero-section">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <div className="lp-animate-in">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#003973]/5 text-xs uppercase tracking-[0.2em] font-bold text-[#003973] mb-8">
                <Clock className="w-3.5 h-3.5 text-[#00B2A9]" strokeWidth={1.5} />
                Regional Staffing Marketplace
              </div>
              <h1 className="font-heading text-5xl md:text-6xl font-bold tracking-tight leading-none text-[#003973] mb-6">
                Staffing made easy for hospitality and events
              </h1>
              <p className="text-base md:text-lg leading-relaxed text-[#4A6380] max-w-lg mb-10 font-body">
                Servello connects businesses with vetted catering, bartending, and event staff. Post a shift, get matched, manage everything — all in one place.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <button onClick={() => openAuth("signup")} className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-sm bg-[#00B2A9] text-white text-sm font-semibold hover:bg-[#00958D] active:scale-[0.98] transition-all duration-200" data-testid="hero-cta-hire">
                  Hire Staff <ArrowRight className="w-4 h-4" />
                </button>
                <button onClick={() => openAuth("signup")} className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-sm border border-[#003973]/20 text-[#003973] text-sm font-semibold hover:bg-[#003973]/5 active:scale-[0.98] transition-all duration-200" data-testid="hero-cta-work">
                  Get Hired!
                </button>
              </div>
              <button onClick={() => navigate("/directory")} className="mt-4 text-sm text-[#00B2A9] hover:text-[#00958D] font-medium transition-colors duration-200" data-testid="hero-directory-link">
                Looking to hire a chef or caterer? Browse our directory &rarr;
              </button>
            </div>
            <div className="lp-animate-in-delay">
              <div className="grid grid-cols-2 md:grid-cols-5 md:grid-rows-2 gap-3 h-auto md:h-[480px]">
                <div className="col-span-2 md:col-span-3 md:row-span-2 rounded-xl overflow-hidden h-[260px] md:h-auto relative group">
                  <img src={IMG.heroMain} alt="Catering staff bustling in event space" fetchpriority="high" width={800} height={480} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                </div>
                <div className="col-span-1 md:col-span-2 rounded-xl overflow-hidden h-[160px] md:h-auto relative group">
                  <img src={IMG.heroAlt} alt="Chef garnishing plates" width={640} height={400} loading="lazy" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                </div>
                <div className="col-span-1 md:col-span-2 rounded-xl bg-[#003973] p-5 md:p-6 flex flex-col justify-center h-[160px] md:h-auto">
                  <p className="text-2xl md:text-3xl font-heading font-bold text-[#00B2A9]">500+</p>
                  <p className="text-xs md:text-sm mt-1 text-white/60">Shifts filled this month</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ─── SOCIAL PROOF BAR ─── */}
      <section className="py-8 border-y border-[#003973]/8" data-testid="stats-bar">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            {[
              { val: "1,200+", label: "Shifts Posted" },
              { val: "500+", label: "Active Workers" },
              { val: "98%", label: "Fill Rate" },
              { val: "4.9", label: "Avg. Rating" },
            ].map((s) => (
              <div key={s.val}>
                <p className="text-2xl md:text-3xl font-heading font-bold text-[#003973]">{s.val}</p>
                <p className="text-xs mt-1 uppercase tracking-widest font-medium text-[#4A6380]">{s.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── HOW IT WORKS ─── */}
      <section id="how-it-works" className="py-24 md:py-32 px-6 md:px-12 lg:px-24 scroll-mt-20" data-testid="how-it-works-section">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <p className="text-xs uppercase tracking-[0.2em] font-bold text-[#00B2A9] mb-4">How It Works</p>
            <h2 className="font-heading text-3xl md:text-5xl font-bold tracking-tight leading-none text-[#003973]">Simple steps, reliable results</h2>
          </div>

          <div className="flex gap-1 rounded-full p-1 bg-[#E1E7ED] w-fit mx-auto mb-16">
            <button
              onClick={() => setActiveTrack("business")}
              className={`px-6 py-2.5 rounded-full text-sm font-medium transition-all duration-300 ${activeTrack === "business" ? "bg-[#003973] text-white shadow-sm" : "text-[#4A6380]"}`}
              data-testid="track-toggle-business"
            >For Businesses</button>
            <button
              onClick={() => setActiveTrack("worker")}
              className={`px-6 py-2.5 rounded-full text-sm font-medium transition-all duration-300 ${activeTrack === "worker" ? "bg-[#003973] text-white shadow-sm" : "text-[#4A6380]"}`}
              data-testid="track-toggle-worker"
            >For Workers</button>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
            <div className="lg:col-span-5 rounded-2xl overflow-hidden h-[300px] lg:h-[420px] relative">
              <img src={trackImage} loading="lazy" width={800} height={420} alt={activeTrack === "business" ? "Chef plating food" : "Bartender crafting cocktail"} className="w-full h-full object-cover transition-opacity duration-500" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#003973]/40 to-transparent" />
              <div className="absolute bottom-6 left-6">
                <span className="inline-block px-3 py-1.5 rounded-full bg-white/90 text-[#003973] text-xs font-bold uppercase tracking-wider">
                  {activeTrack === "business" ? "For Businesses" : "For Workers"}
                </span>
              </div>
            </div>
            <div className="lg:col-span-7 space-y-6">
              {steps.map((step) => (
                <div key={step.num} className="flex gap-5 p-6 md:p-8 rounded-xl bg-white border border-[#003973]/8 hover:shadow-lg hover:-translate-y-0.5 transition-all duration-300" data-testid={`step-${step.num}`}>
                  <div className="shrink-0">
                    <div className="w-12 h-12 rounded-xl bg-[#00B2A9]/8 flex items-center justify-center">
                      <step.icon className="w-5 h-5 text-[#00B2A9]" strokeWidth={1.5} />
                    </div>
                  </div>
                  <div>
                    <div className="flex items-center gap-3 mb-2">
                      <span className="text-xs font-bold uppercase tracking-widest text-[#E1E7ED]">{step.num}</span>
                      <h3 className="font-heading text-lg font-semibold text-[#003973]">{step.title}</h3>
                    </div>
                    <p className="text-sm leading-relaxed font-body text-[#4A6380]">{step.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ─── FEATURES ─── */}
      <section id="features" className="py-24 md:py-32 px-6 md:px-12 lg:px-24 bg-white scroll-mt-20" data-testid="features-section">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-xs uppercase tracking-[0.2em] font-bold text-[#00B2A9] mb-4">Platform Features</p>
            <h2 className="font-heading text-3xl md:text-5xl font-bold tracking-tight leading-none text-[#003973]">Built for the hospitality industry</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-12 md:gap-y-20">
            {FEATURES.map((f, i) => (
              <div key={f.title} className={`flex gap-6 ${i % 2 === 1 ? "md:mt-12" : ""}`}>
                <div className="shrink-0">
                  <span className="text-5xl font-heading font-bold text-[#E1E7ED] select-none">0{i + 1}</span>
                </div>
                <div>
                  <f.icon className="w-6 h-6 text-[#00B2A9] mb-3" strokeWidth={1.5} />
                  <h3 className="font-heading text-xl font-semibold text-[#003973] mb-2">{f.title}</h3>
                  <p className="text-sm leading-relaxed font-body text-[#4A6380]">{f.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── TESTIMONIAL ─── */}
      <section className="py-24 md:py-32 px-6 md:px-12 lg:px-24" data-testid="testimonial-section">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-0 rounded-2xl overflow-hidden shadow-xl min-h-[440px]">
            <div className="relative h-[300px] lg:h-auto">
              <img src={IMG.waiter} loading="lazy" width={800} height={440} alt="Hospitality worker smiling" className="w-full h-full object-cover" />
            </div>
            <div className="bg-[#003973] p-10 md:p-16 flex flex-col justify-center">
              <Star className="w-8 h-8 text-[#00B2A9] mb-6" strokeWidth={1.5} />
              <blockquote className="font-serif text-2xl md:text-3xl italic leading-relaxed text-white/90 mb-8">
                "Servello changed how I find work. I pick my shifts, show up, and get paid — simple as that. The ratings system motivates me to deliver my best every time."
              </blockquote>
              <div>
                <p className="font-medium text-white">Marcus J.</p>
                <p className="text-sm text-white/50">Event Server &middot; Miami, FL</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ─── ACTION GALLERY ─── */}
      <section className="px-6 md:px-12 lg:px-24 pb-24 md:pb-32" data-testid="gallery-section">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-4">
            <div className="rounded-xl overflow-hidden h-[200px] md:h-[280px] relative group">
              <img src={IMG.caterer} loading="lazy" alt="Caterer arranging food service" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#003973]/60 to-transparent" />
              <p className="absolute bottom-4 left-4 text-white text-xs font-bold uppercase tracking-wider">Catering</p>
            </div>
            <div className="rounded-xl overflow-hidden h-[200px] md:h-[280px] relative group">
              <img src={IMG.bartender} loading="lazy" width={640} height={280} alt="Bartender crafting cocktail" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#003973]/60 to-transparent" />
              <p className="absolute bottom-4 left-4 text-white text-xs font-bold uppercase tracking-wider">Bartending</p>
            </div>
            <div className="col-span-2 md:col-span-1 rounded-xl overflow-hidden h-[200px] md:h-[280px] relative group">
              <img src={IMG.chef} loading="lazy" width={640} height={280} alt="Chef at work in kitchen" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#003973]/60 to-transparent" />
              <p className="absolute bottom-4 left-4 text-white text-xs font-bold uppercase tracking-wider">Private Chef</p>
            </div>
          </div>
        </div>
      </section>

      {/* ─── SPLIT CTA ─── */}
      <section className="grid grid-cols-1 md:grid-cols-2" data-testid="split-cta-section">
        <div className="bg-[#003973] px-8 md:px-16 py-20 md:py-28 flex flex-col items-center justify-center text-center">
          <Briefcase className="w-10 h-10 text-white/20 mb-6" strokeWidth={1.5} />
          <h3 className="font-heading text-3xl md:text-4xl font-bold text-white mb-4">Hire Staff</h3>
          <p className="text-white/60 mb-8 max-w-sm text-sm leading-relaxed font-body">
            Post shifts and fill your roster with vetted hospitality professionals. Rush fees, promo codes, and favorites built in.
          </p>
          <button onClick={() => openAuth("signup")} className="px-8 py-4 rounded-sm bg-white text-[#003973] text-sm font-semibold hover:bg-white/90 active:scale-[0.98] transition-all duration-200" data-testid="split-cta-business">
            Get Started as a Business <ArrowRight className="w-4 h-4 inline ml-2" />
          </button>
        </div>
        <div className="bg-[#00B2A9] px-8 md:px-16 py-20 md:py-28 flex flex-col items-center justify-center text-center">
          <Users className="w-10 h-10 text-white/20 mb-6" strokeWidth={1.5} />
          <h3 className="font-heading text-3xl md:text-4xl font-bold text-white mb-4">Find Work</h3>
          <p className="text-white/80 mb-8 max-w-sm text-sm leading-relaxed font-body">
            Browse shifts in your area, build your reputation, and earn on your own schedule. Background checks and badges included.
          </p>
          <button onClick={() => openAuth("signup")} className="px-8 py-4 rounded-sm bg-white text-[#00B2A9] text-sm font-semibold hover:bg-white/90 active:scale-[0.98] transition-all duration-200" data-testid="split-cta-worker">
            Get Started as a Worker <ArrowRight className="w-4 h-4 inline ml-2" />
          </button>
        </div>
      </section>

      {/* ─── FOOTER ─── */}
      <footer className="py-16 px-6 md:px-12 lg:px-24 border-t border-[#003973]/8 bg-[#F5F7FA]" data-testid="landing-footer">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-6">
          <img src={LOGO_URL} alt="Servello" className="h-10 object-contain" />
          <div className="flex items-center gap-8">
            <button onClick={() => scrollTo("how-it-works")} className="text-xs text-[#4A6380] hover:text-[#003973] transition-colors">How it Works</button>
            <button onClick={() => scrollTo("features")} className="text-xs text-[#4A6380] hover:text-[#003973] transition-colors">Features</button>
            <button onClick={() => navigate("/for-workers")} className="text-xs text-[#4A6380] hover:text-[#003973] transition-colors">For Workers</button>
            <button onClick={() => navigate("/for-businesses")} className="text-xs text-[#4A6380] hover:text-[#003973] transition-colors">For Businesses</button>
            <button onClick={() => navigate("/blog")} className="text-xs text-[#4A6380] hover:text-[#003973] transition-colors">Blog</button>
          </div>
          <div className="flex flex-col items-center md:items-end gap-1">
            <a href="tel:8556673783" className="text-xs font-semibold text-[#003973] hover:text-[#00B2A9] transition-colors" data-testid="footer-phone">855.66.SERVELLO</a>
            <p className="text-xs font-body text-[#4A6380]">Servello by Tylegn. All rights reserved.</p>
          </div>
        </div>
      </footer>

      {/* ─── AUTH MODAL ─── */}
      {showAuth && (
        <div className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center p-0 sm:p-4 bg-black/50 backdrop-blur-sm" onClick={() => setShowAuth(false)} data-testid="auth-modal-overlay">
          <div className="bg-white border border-[#003973]/10 rounded-t-2xl sm:rounded-2xl shadow-2xl w-full max-w-md p-6 sm:p-8 relative max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="auth-modal">
            <button onClick={() => setShowAuth(false)} className="absolute top-4 right-4 p-1.5 rounded-full hover:bg-[#F5F7FA] text-[#4A6380] transition-colors duration-200" data-testid="auth-modal-close">
              <X className="w-4 h-4" />
            </button>
            <div className="text-center mb-6">
              <img src={LOGO_URL} alt="Servello" className="h-8 mx-auto mb-3" />
              <h2 className="font-heading text-xl font-bold text-[#003973]">{authMode === "signin" ? "Welcome back" : "Create your account"}</h2>
              <p className="text-sm mt-1 text-[#4A6380]">{authMode === "signin" ? "Sign in to your Servello account" : "Join Servello today"}</p>
            </div>

            <button onClick={handleGoogleFromModal} data-testid="google-auth-btn"
              className="w-full flex items-center justify-center gap-3 px-4 py-2.5 rounded-sm border border-[#003973]/15 hover:bg-[#F5F7FA] transition-colors duration-200 mb-4">
              <svg className="w-5 h-5" viewBox="0 0 24 24"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/><path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/></svg>
              <span className="text-sm font-medium text-[#003973]">Continue with Google</span>
            </button>

            <div className="relative mb-4">
              <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-[#003973]/10"></div></div>
              <div className="relative flex justify-center text-xs"><span className="bg-white px-3 text-[#4A6380]">or</span></div>
            </div>

            <form onSubmit={handleEmailAuth} className="space-y-3">
              {authMode === "signup" && (
                <div className="space-y-1.5">
                  <Label className="text-xs text-[#003973]">Full Name</Label>
                  <Input placeholder="John Doe" value={authForm.name} required
                    onChange={e => setAuthForm(p => ({...p, name: e.target.value}))} data-testid="auth-name-input" className="rounded-sm" />
                </div>
              )}
              <div className="space-y-1.5">
                <Label className="text-xs text-[#003973]">Email</Label>
                <Input type="email" placeholder="you@example.com" value={authForm.email} required
                  onChange={e => setAuthForm(p => ({...p, email: e.target.value}))} data-testid="auth-email-input" className="rounded-sm" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs text-[#003973]">Password</Label>
                <Input type="password" placeholder={authMode === "signup" ? "Min 6 characters" : "Your password"} value={authForm.password} required minLength={6}
                  onChange={e => setAuthForm(p => ({...p, password: e.target.value}))} data-testid="auth-password-input" className="rounded-sm" />
              </div>
              <button type="submit" disabled={authLoading} data-testid="auth-submit-btn"
                className="w-full py-3 rounded-sm bg-[#00B2A9] text-white text-sm font-semibold hover:bg-[#00958D] active:scale-[0.98] transition-all duration-200 disabled:opacity-50">
                {authLoading ? "Please wait..." : authMode === "signin" ? "Sign In" : "Create Account"}
              </button>
            </form>

            <p className="text-center text-xs text-[#4A6380] mt-4">
              {authMode === "signin" ? (
                <>Don't have an account?{" "}<button onClick={() => setAuthMode("signup")} className="font-medium text-[#00B2A9] hover:underline" data-testid="auth-mode-toggle">Sign up</button></>
              ) : (
                <>Already have an account?{" "}<button onClick={() => setAuthMode("signin")} className="font-medium text-[#00B2A9] hover:underline" data-testid="auth-mode-toggle">Sign in</button></>
              )}
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
